import { useContext, useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import TextField from '@mui/material/TextField'
import { object } from 'prop-types'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsAutoComplete } from '@components'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'
import { connectorContext, folderContext } from '@contexts'
import { useAppSelector } from '@redux/hooks'
import { templateCopyContext } from '../context'

const FullWidth = styled.div`
  width: 100%;
`

export const SFTPInbound = ({ assignment }) => {
  const { template, onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const { byConnectorId } = useContext(folderContext)
  const [ENABLE_AUTO_SUBMIT = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.ENABLE_AUTO_SUBMIT]),
  )

  const isAutoSubmitEnabled = useMemo(
    () => ENABLE_AUTO_SUBMIT && !!template?.template?.settings?.enable_auto_submit,
    [ENABLE_AUTO_SUBMIT, template?.template?.settings?.enable_auto_submit],
  )

  const connectorId = useMemo(
    () => assignment?.connector_id ?? assignment?.connectorId,
    [assignment.connector_id, assignment.connectorId],
  )

  const folders = useMemo(
    () => (byConnectorId?.[connectorId] || []).map(f => ({ label: f.name, value: f.id })),
    [byConnectorId, connectorId],
  )

  const connectors = useMemo(
    () =>
      Object.values(byId)
        ?.filter(
          connector =>
            connector.connector_type_id ===
            (assignment.connector_type_id
              ? assignment.connector_type_id
              : byId[connectorId].connector_type_id),
        )
        ?.map(({ name, id }) => ({
          label: name,
          value: id,
        })) || [],
    [assignment.connector_type_id, byId, connectorId],
  )

  const fields = useMemo(
    () =>
      Object.values(template?.fieldsById)
        ?.filter(field => field.name !== 'Claravine ID')
        ?.map(field => ({
          label: field.name,
          value: field.id,
        })) || [],
    [template.fieldsById],
  )

  const onSFTPChange = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          [name]: value,
        },
      }))
    },
    [assignment.id, onChange],
  )

  const onSFTPMetadataChange = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            [name]: value,
          },
        },
      }))
    },
    [assignment.id, onChange],
  )

  const onIdentifierFieldIdsChange = useCallback(
    value => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            identifier_field_ids: value.join(','),
          },
        },
      }))
    },
    [assignment.id, onChange],
  )

  const identifierFieldIds = useMemo(
    () => assignment?.metadata?.identifier_field_ids?.split(',') ?? [],
    [assignment.metadata.identifier_field_ids],
  )

  return (
    <FullWidth>
      <CvsAutoComplete
        options={connectors || []}
        label={t`SFTP account`}
        selected={connectorId}
        onSelected={value => onSFTPChange(value, 'connector_id')}
      />
      <TextField
        options={folders || []}
        label={t`Source folder (path from home directory to target folder, without leading or trailing slashes)`}
        style={{ width: '100%' }}
        value={assignment.metadata.folder}
        onChange={e => onSFTPMetadataChange(e.target.value, 'folder')}
        variant="outlined"
      />
      {isAutoSubmitEnabled && (
        <CvsAutoComplete
          options={fields || []}
          label={t`Unique identifier field`}
          selected={formatSelectedOptions(true, identifierFieldIds, fields || [])}
          onSelected={onIdentifierFieldIdsChange}
          isMulti={true}
          required={true}
        />
      )}
    </FullWidth>
  )
}

SFTPInbound.propTypes = {
  assignment: object.isRequired,
}
